import { useMemo } from "react";
import type { Page } from "../lib/types";

/**
 * The site as the crawler walks it.
 *
 * Two halves, and the second one is the point. The top half is the navigation
 * tree — what a visitor can reach by clicking. The bottom half is every URL the
 * sitemap knows about that nothing links to: a page built and indexed but not
 * yet revealed. That is what a drop looks like before it is a drop.
 */

const STYLE_ID = "aw-nav-graph-styles";

const CSS = `
.aw-ng {
  --ink: #0D0D0D;
  --red: #FF0000;
  --pink: #E89AC7;
  --tag: #FFFFFF;
  --rule: rgba(13, 13, 13, 0.14);
  --mute: #6B6B6B;
  --indent: 24px;
  --anton: "Anton", "Oswald", Impact, Haettenschweiler, sans-serif;
  --oswald: "Oswald", "Anton", "Arial Narrow", Arial, sans-serif;
  --sora: "Sora", "Helvetica Neue", Helvetica, Arial, sans-serif;
  color: var(--ink);
}

.aw-ng-panel {
  background: var(--tag);
  border: 1px solid var(--ink);
  padding: 18px 20px;
}

.aw-ng-list { list-style: none; margin: 0; padding: 0; }
.aw-ng-list .aw-ng-list { margin: 0; }

.aw-ng-row {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid var(--rule);
}
.aw-ng-list > li:first-child > .aw-ng-row { border-top: 0; }

/* A short tick standing in for the branch line; the store's own type is flat, so is this. */
.aw-ng-rail {
  flex: 0 0 auto;
  width: 12px;
  margin-top: 9px;
  border-top: 1px solid var(--ink);
  opacity: 0.45;
}
.aw-ng-rail.is-root { border-top-color: transparent; width: 0; margin: 0; }

.aw-ng-body { min-width: 0; flex: 1 1 auto; }

.aw-ng-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.aw-ng-path {
  font-family: var(--oswald);
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.01em;
  overflow-wrap: anywhere;
}
.aw-ng-path.is-gone { text-decoration: line-through; color: var(--mute); }

.aw-ng-chip {
  font-family: var(--sora);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 2px 6px 1px;
  border: 1px solid var(--rule);
  color: var(--mute);
  white-space: nowrap;
}
.aw-ng-chip.is-role { border-color: var(--ink); color: var(--ink); }
.aw-ng-chip.is-sitemap { border-color: var(--pink); color: var(--ink); background: rgba(232, 154, 199, 0.22); }
.aw-ng-chip.is-goneflag { border-color: var(--red); color: var(--red); }

.aw-ng-status {
  font-family: var(--oswald);
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
}
.aw-ng-status.is-ok { color: var(--ink); }
.aw-ng-status.is-redirect { color: var(--mute); }
.aw-ng-status.is-bad { color: var(--red); }
.aw-ng-status.is-none { color: var(--mute); }

.aw-ng-doctitle {
  margin-top: 3px;
  font-family: var(--sora);
  font-size: 11px;
  color: var(--mute);
  overflow-wrap: anywhere;
}

.aw-ng-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
}
.aw-ng-label {
  font-family: var(--sora);
  font-size: 9px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--mute);
}

.aw-ng-ids {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.aw-ng-id {
  font-family: var(--oswald);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.03em;
  padding: 1px 5px 0;
  border: 1px solid var(--rule);
  color: var(--ink);
  white-space: nowrap;
}
/* Sold-out ids are struck through, the way an unavailable size is on a hangtag. */
.aw-ng-id.is-soldout { color: var(--red); border-color: rgba(255, 0, 0, 0.35); text-decoration: line-through; }

/* ---- the pre-drop signal ---- */
.aw-ng-orphans {
  margin-top: 34px;
  border: 2px solid var(--ink);
  background: var(--tag);
  box-shadow: 8px 8px 0 rgba(232, 154, 199, 0.45);
}
.aw-ng-orphans-head {
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--ink);
  background: rgba(232, 154, 199, 0.16);
}
.aw-ng-swirl { display: block; width: 104px; height: 22px; margin-bottom: 6px; }
.aw-ng-orphans-title {
  margin: 0;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  font-family: var(--anton);
  font-size: clamp(18px, 3.4vw, 24px);
  font-weight: 400;
  line-height: 1;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.aw-ng-orphans-count {
  font-family: var(--oswald);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.14em;
  padding: 3px 8px 2px;
  background: var(--ink);
  color: var(--tag);
}
.aw-ng-orphans-note {
  margin: 8px 0 0;
  font-family: var(--sora);
  font-size: 11px;
  line-height: 1.5;
  color: var(--ink);
  max-width: 62ch;
}
.aw-ng-orphans-body { padding: 4px 20px 16px; }
.aw-ng-orphans .aw-ng-row { padding: 12px 0; }

.aw-ng-section-title {
  margin: 0 0 12px;
  font-family: var(--anton);
  font-size: clamp(15px, 2.6vw, 18px);
  font-weight: 400;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.aw-ng-empty {
  margin: 0;
  padding: 22px 0;
  font-family: var(--sora);
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--mute);
  text-align: center;
}

@media (max-width: 640px) {
  .aw-ng { --indent: 12px; }
  .aw-ng-panel { padding: 14px; }
  .aw-ng-orphans-head { padding: 14px; }
  .aw-ng-orphans-body { padding: 4px 14px 12px; }
  .aw-ng-orphans { box-shadow: 5px 5px 0 rgba(232, 154, 199, 0.45); }
}
`;

if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

/** Indentation is capped so a deep chain can never push the row off-screen. */
const MAX_INDENT_LEVELS = 6;

const isOrphan = (page: Page): boolean => page.orphaned || page.depth < 0;

const statusClass = (status?: number): string => {
  if (typeof status !== "number") return "is-none";
  if (status < 300) return "is-ok";
  if (status < 400) return "is-redirect";
  return "is-bad";
};

interface TreeNode {
  page: Page;
  children: TreeNode[];
}

/**
 * Builds the click-tree from `linkedFrom`, falling back to a flat depth
 * ordering for anything whose parent was not crawled. Parents always have a
 * strictly smaller depth than their children, so the result cannot cycle.
 */
const buildTree = (pages: Page[]): TreeNode[] => {
  const ordered = [...pages].sort((a, b) => a.depth - b.depth || a.path.localeCompare(b.path));
  const nodes = new Map<string, TreeNode>();
  for (const page of ordered) nodes.set(page.path, { page, children: [] });

  const roots: TreeNode[] = [];
  for (const page of ordered) {
    const node = nodes.get(page.path);
    if (!node) continue;

    const parentPath =
      page.depth > 0
        ? (page.linkedFrom.find((from) => nodes.get(from)?.page.depth === page.depth - 1) ??
          page.linkedFrom.find((from) => {
            const candidate = nodes.get(from);
            return candidate !== undefined && candidate.page.depth < page.depth;
          }))
        : undefined;

    const parent = parentPath ? nodes.get(parentPath) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
};

const flatten = (nodes: TreeNode[], level = 0): Array<{ page: Page; level: number }> =>
  nodes.flatMap((node) => [{ page: node.page, level }, ...flatten(node.children, level + 1)]);

function PageRow({ page, level }: { page: Page; level: number }) {
  const via = Array.from(new Set(page.discoveredVia));
  const products = page.products ?? [];
  const indent = `calc(var(--indent) * ${Math.min(level, MAX_INDENT_LEVELS)})`;

  return (
    <div className="aw-ng-row" style={{ marginLeft: indent }}>
      <span className={`aw-ng-rail${level === 0 ? " is-root" : ""}`} aria-hidden="true" />
      <div className="aw-ng-body">
        <div className="aw-ng-head">
          <span className={`aw-ng-path${page.gone ? " is-gone" : ""}`}>{page.path}</span>
          <span className="aw-ng-chip is-role">{page.role}</span>
          <span className={`aw-ng-status ${statusClass(page.httpStatus)}`}>
            {typeof page.httpStatus === "number" ? page.httpStatus : "no response"}
          </span>
          {page.gone ? <span className="aw-ng-chip is-goneflag">gone</span> : null}
        </div>

        <div className="aw-ng-meta">
          <span className="aw-ng-label">found via</span>
          {via.length > 0 ? (
            via.map((source) => (
              <span
                key={source}
                className={`aw-ng-chip${source === "sitemap" ? " is-sitemap" : ""}`}
              >
                {source}
              </span>
            ))
          ) : (
            <span className="aw-ng-chip">unknown</span>
          )}
          <span className="aw-ng-label">
            {products.length === 1 ? "renders 1 product" : `renders ${products.length} products`}
          </span>
        </div>

        {products.length > 0 ? (
          <div className="aw-ng-ids">
            {products.map((product) => (
              <span
                key={product.productId}
                className={`aw-ng-id${product.soldOut ? " is-soldout" : ""}`}
                title={product.title ?? undefined}
              >
                {product.productId}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export interface NavGraphProps {
  pages: Page[];
}

export function NavGraph({ pages }: NavGraphProps) {
  const { linked, orphans } = useMemo(() => {
    const linkedPages = pages.filter((page) => !isOrphan(page));
    const orphanPages = pages
      .filter(isOrphan)
      .sort((a, b) => a.role.localeCompare(b.role) || a.path.localeCompare(b.path));
    return { linked: flatten(buildTree(linkedPages)), orphans: orphanPages };
  }, [pages]);

  return (
    <div className="aw-ng">
      <div className="aw-ng-panel">
        <h3 className="aw-ng-section-title">Reachable from the navigation</h3>
        {linked.length > 0 ? (
          <ul className="aw-ng-list">
            {linked.map((entry) => (
              <li key={entry.page.path}>
                <PageRow page={entry.page} level={entry.level} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="aw-ng-empty">No pages crawled yet</p>
        )}
      </div>

      <section className="aw-ng-orphans" aria-label="Pages not linked from navigation">
        <div className="aw-ng-orphans-head">
          <svg className="aw-ng-swirl" viewBox="0 0 120 24" aria-hidden="true">
            <path
              d="M2 17C10 3 21 2 26 12s13 12 19 3 15-11 21-2 16 11 23 1c3-5 9-5 13 0"
              fill="none"
              stroke="#E89AC7"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          </svg>
          <h3 className="aw-ng-orphans-title">
            Not linked from navigation
            <span className="aw-ng-orphans-count">{orphans.length}</span>
          </h3>
          <p className="aw-ng-orphans-note">
            These URLs are in the sitemap but nothing on the site links to them — a page built and
            indexed before it has been revealed, which is what a drop looks like from here.
          </p>
        </div>
        <div className="aw-ng-orphans-body">
          {orphans.length > 0 ? (
            <ul className="aw-ng-list">
              {orphans.map((page) => (
                <li key={page.path}>
                  <PageRow page={page} level={0} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="aw-ng-empty">Every known URL is reachable — nothing waiting</p>
          )}
        </div>
      </section>
    </div>
  );
}

export default NavGraph;
